/**
 * Consent Timeline View Component
 *
 * Displays chronological timeline of consent journey events using Equal DS Timeline.
 * Shows timestamps, event titles, descriptions, transaction IDs, and status badges.
 */

import React from 'react';
import { Timeline } from 'equal-ds-ui';
import { Clock } from 'lucide-react';
import type { ConsentTimeline } from '../../../types/timeline';

interface ConsentTimelineViewProps {
  timeline: ConsentTimeline | null;
}

export const ConsentTimelineView: React.FC<ConsentTimelineViewProps> = ({ timeline }) => {
  // Handle empty state
  if (!timeline || timeline.events.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-text-tertiary">
        <p>No consent timeline events available.</p>
      </div>
    );
  }

  const { events, overallTAT } = timeline;

  // For 1-3 events: fit content height
  // For 4+ events: use flex-1 with scroll (matching DataTimelineView)
  const useFlexHeight = events.length >= 4;

  return (
    <div className={`flex flex-col space-y-3 ${useFlexHeight ? 'h-full' : ''}`}>
      {/* Timeline Header with TAT */}
      <div className="flex items-center justify-between mb-1.5 flex-shrink-0">
        <h3 className="text-sm font-medium text-text-primary tracking-wider">CONSENT TIMELINE</h3>
        {overallTAT?.duration && (
          <div className="flex items-center gap-1">
            <span className="text-sm font-medium text-text-muted tracking-wider">OVERALL TAT:</span>
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3 text-text-primary" />
              <span className="text-sm font-medium text-text-primary tracking-wide">
                {overallTAT.duration}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Timeline - dynamic height for few items, flex with scroll for many */}
      <div className={`bg-background-secondary border border-border-light rounded-xl p-4 ${
        useFlexHeight ? 'flex-1 min-h-0 overflow-y-auto' : ''
      }`}>
        <Timeline
          items={events as any}
          variant="default"
          showConnectors={true}
          connectorGap={2}
          className={useFlexHeight ? '!justify-start gap-6' : '!justify-start !h-auto gap-6'}
        />
      </div>
    </div>
  );
};
